import type { ApolloError } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../../data';
import {
  updateApproachability,
  updateApproachabilityFailure,
  updateApproachabilitySuccess,
} from '../actionCreators';

import translations from './.vocab';
import {
  generateUpdateApproachabilityOptimisticResponse,
  updateCacheUpdateApproachability,
} from './apolloCache';

export const useApproachability = () => {
  const { data, loading, error } = useQuery(Schema.GetApproachabilityDocument);

  const approachable = data?.viewer?.approachability?.approachable ?? true;

  return { approachable, loading, error };
};

const key = 'next-role-approachability';

export const useUpdateApproachability = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = ({
    approachability: { approachable },
  }: Schema.UpdateApproachabilityMutationVariables) => {
    dispatch(updateApproachability({ approachable }));
  };
  const onCompleted = () => {
    dispatch(updateApproachabilitySuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateApproachabilityFailure(error));
  };

  return useToastMutation(Schema.UpdateApproachabilityDocument, {
    key,
    errorActionRouteParameters: {
      to: 'approachability',
      mode: 'EDIT',
    },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Approachability updated'),
    update: updateCacheUpdateApproachability,
    optimisticResponse: generateUpdateApproachabilityOptimisticResponse({
      _id: candidateId || '',
    }),
  });
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetApproachabilityRequest = (
  approachability: Schema.Approachability,
) => ({
  request: {
    query: Schema.GetApproachabilityDocument,
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        approachability,
      },
    },
  },
});
